import { applySnapshot, flow, Instance, types } from 'mobx-state-tree';
import { isAbortError } from 'utils';
import { NewNews as News } from 'http-requests';
import {
  CreateNewsFeedDTO,
  GetNewsCountResponseDTO,
  GetNewsResponseDTO,
  GetUnreadNewsResponseDTO,
  NewsFeedDTO,
  NewsFeedFiltersDTO,
} from 'http-requests/news';
import { NewsFeedItemModel } from './model';
import { NewsConstantsModel } from './constants.model';
import { mapNewsFeedItem } from './mapper';
import { RequestStateModel } from '../general-models';

const TabCountModel = types.model('TabCountModel', {
  total: types.optional(types.number, 0),
  unread: types.optional(types.number, 0),
});

const NewsCountsModel = types.model('NewsCountsModel', {
  published: types.optional(TabCountModel, {}),
  draft: types.optional(TabCountModel, {}),
  scheduled: types.optional(TabCountModel, {}),
  status: types.optional(RequestStateModel, {}),
});

export interface INewsCountsModel extends Instance<typeof NewsCountsModel> {}

const TabFeedModel = types
  .model('TabFeedModel', {
    feeds: types.optional(types.array(NewsFeedItemModel), []),
    actualEvents: types.optional(types.array(NewsFeedItemModel), []),
    next: types.optional(types.maybeNull(types.string), null),
    actualEventsNext: types.optional(types.maybeNull(types.string), null),
    status: types.optional(RequestStateModel, {}),
    fetchMoreStatus: types.optional(RequestStateModel, {}),
  })
  .views((self) => ({
    get hasFeeds() {
      return !!self.feeds.length || !!self.actualEvents.length;
    },
    get hasMore() {
      return self.next !== null;
    },
    get hasMoreActualEvents() {
      return self.actualEventsNext !== null;
    },
  }));

export const NewsFeedStore = types
  .model('NewsFeedStore', {
    published: types.optional(TabFeedModel, {}),
    draft: types.optional(TabFeedModel, {}),
    scheduled: types.optional(TabFeedModel, {}),
    counts: types.optional(NewsCountsModel, {}),
    constants: types.optional(NewsConstantsModel, {}),
    unreadIds: types.optional(types.array(types.string), []),
  })
  .volatile(() => ({
    _fetchAbortController: null as AbortController | null,
    _getByIdAbortController: null as AbortController | null,
  }))
  .views((self) => ({
    get unreadIdsSet(): ReadonlySet<string> {
      return new Set(self.unreadIds);
    },
    get defaultFilters(): NewsFeedFiltersDTO {
      return {
        limit: 20,
        offset: 0,
        state: 'published',
      };
    },
  }))
  .actions((self) => ({
    cancelFetch() {
      self._fetchAbortController?.abort();
    },

    removeItem(id: string, state: 'published' | 'draft' | 'scheduled') {
      const tab = self[state];

      const feedIdx = tab.feeds.findIndex((f) => f.id === id);

      if (feedIdx !== -1) {
        tab.feeds.splice(feedIdx, 1);

        return;
      }

      const evtIdx = tab.actualEvents.findIndex((e) => e.id === id);

      if (evtIdx !== -1) {
        tab.actualEvents.splice(evtIdx, 1);
      }
    },

    fetchUnreadIds: flow(function* () {
      self._fetchAbortController?.abort();
      self._fetchAbortController = new AbortController();
      const { signal } = self._fetchAbortController;

      const response: GetUnreadNewsResponseDTO =
        yield News.GetUnreadNews(signal);

      self.unreadIds.replace(response.results.map((r) => r.id));
    }),

    markAsRead(id: string) {
      const idx = self.unreadIds.indexOf(id);

      if (idx === -1) {
        return;
      }

      self.unreadIds.splice(idx, 1);

      if (self.counts.published.unread > 0) {
        self.counts.published.unread--;
      }

      void News.MarkNewsAsRead(id);
    },

    fetchNewsCounts: flow(function* () {
      try {
        self.counts.status.loadingStates();
        const response: GetNewsCountResponseDTO = yield News.GetNewsCount();

        self.counts.published = TabCountModel.create(response.all);
        self.counts.draft = TabCountModel.create(response.drafts);
        self.counts.scheduled = TabCountModel.create(response.scheduled);

        self.counts.status.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        self.counts.status.errorStates();
        console.error(error);
      } finally {
        self.counts.status.finallyStates();
      }
    }),

    fetchMoreActualEvents: flow(function* (filters: NewsFeedFiltersDTO) {
      const feedState = self.published;

      try {
        feedState.fetchMoreStatus.loadingStates();

        const response: GetNewsResponseDTO = yield News.GetActualEvents({
          params: { ...filters, offset: feedState.actualEvents.length },
        });

        feedState.actualEvents.push(
          ...response.results.map((dto: NewsFeedDTO) =>
            NewsFeedItemModel.create(mapNewsFeedItem(dto))
          )
        );
        feedState.actualEventsNext = response.next;

        if (response.results.length < (filters.limit ?? 20)) {
          const actualEventIds = new Set(
            feedState.actualEvents.map((e) => e.id)
          );

          const feedsResponse: GetNewsResponseDTO = yield News.GetNews({
            params: { ...filters, offset: 0 },
          });

          feedState.feeds.replace(
            feedsResponse.results
              .filter((dto: NewsFeedDTO) => !actualEventIds.has(dto.id))
              .map((dto: NewsFeedDTO) =>
                NewsFeedItemModel.create(mapNewsFeedItem(dto))
              )
          );
          feedState.next = feedsResponse.next;
        }

        feedState.fetchMoreStatus.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        feedState.fetchMoreStatus.errorStates();
        console.error(error);
      } finally {
        feedState.fetchMoreStatus.finallyStates();
      }
    }),

    fetchMoreFeeds: flow(function* (filters: NewsFeedFiltersDTO) {
      const feedState = self[filters.state];

      try {
        feedState.fetchMoreStatus.loadingStates();

        const actualEventIds = new Set(feedState.actualEvents.map((e) => e.id));

        const response: GetNewsResponseDTO = yield News.GetNews({
          params: filters,
        });

        const newItems = response.results
          .filter((dto: NewsFeedDTO) => !actualEventIds.has(dto.id))
          .map((dto: NewsFeedDTO) =>
            NewsFeedItemModel.create(mapNewsFeedItem(dto))
          );

        if (newItems.length) {
          feedState.feeds.push(...newItems);
        }

        feedState.next = response.next;

        feedState.fetchMoreStatus.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        feedState.fetchMoreStatus.errorStates();
        console.error(error);
      } finally {
        feedState.fetchMoreStatus.finallyStates();
      }
    }),

    fetchActualEvents: flow(function* (
      options: { filters?: NewsFeedFiltersDTO; silent?: boolean } = {}
    ) {
      const { filters = self.defaultFilters, silent = false } = options;

      try {
        self._fetchAbortController?.abort();
        self._fetchAbortController = new AbortController();
        const { signal } = self._fetchAbortController;

        const response: GetNewsResponseDTO = yield News.GetActualEvents({
          params: filters,
          signal,
        });

        const events = response.results.map((dto: NewsFeedDTO) =>
          NewsFeedItemModel.create(mapNewsFeedItem(dto))
        );

        if (!silent) {
          self.published.actualEvents.replace(events);
          self.published.actualEventsNext = response.next;
        }

        return events;
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        return Promise.reject(error);
      }
    }),

    fetchFeeds: flow(function* (
      tab: 'published' | 'draft' | 'scheduled',
      filters: NewsFeedFiltersDTO = self.defaultFilters,
      excludeIds?: ReadonlySet<string>
    ) {
      try {
        self._fetchAbortController?.abort();
        self._fetchAbortController = new AbortController();
        const { signal } = self._fetchAbortController;

        self[tab].status.loadingStates();

        const response: GetNewsResponseDTO = yield News.GetNews({
          params: filters,
          signal,
        });

        self[tab].feeds.replace(
          response.results
            .filter((dto: NewsFeedDTO) => !excludeIds?.has(dto.id))
            .map((dto: NewsFeedDTO) =>
              NewsFeedItemModel.create(mapNewsFeedItem(dto))
            )
        );
        self[tab].next = response.next;
        self[tab].status.successStates();

        return response;
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        self[tab].status.successStates();
        console.error(error);

        return Promise.reject(error);
      } finally {
        self[tab].status.finallyStates();
      }
    }),
  }))
  .actions((self) => ({
    afterCreate() {
      if (typeof document === 'undefined') {
        return;
      }

      const onVisibilityChange = () => {
        if (
          document.visibilityState === 'visible' &&
          !self.counts.status.pending
        ) {
          void self.fetchNewsCounts();
        }
      };

      document.addEventListener('visibilitychange', onVisibilityChange);
    },

    fetch: flow(function* (filters?: NewsFeedFiltersDTO) {
      const tab = filters?.state ?? 'published';
      const baseFilters = filters ?? { limit: 20, offset: 0, state: tab };

      try {
        self[tab].status.loadingStates();

        if (tab !== 'published') {
          yield self.fetchFeeds(tab, baseFilters);
          self[tab].status.successStates();

          return;
        }

        yield self.fetchUnreadIds();

        const typeIn = baseFilters.type__in;
        const shouldFetchEvents = !typeIn?.length || typeIn.includes('event');

        if (!shouldFetchEvents) {
          self.published.actualEvents.replace([]);
          self.published.actualEventsNext = null;

          yield self.fetchFeeds('published', baseFilters);
          self[tab].status.successStates();

          return;
        }

        yield self.fetchActualEvents({
          filters: baseFilters,
        });

        if (self.published.actualEvents.length < baseFilters.limit) {
          const actualEventIds = new Set(
            self.published.actualEvents.map((e) => e.id)
          );

          yield self.fetchFeeds('published', baseFilters, actualEventIds);
        } else {
          self.published.feeds.replace([]);
          self.published.next = null;
        }

        self[tab].status.successStates();
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }

        self[tab].status.errorStates();
        console.error(error);
      } finally {
        self[tab].status.finallyStates();
      }
    }),

    fetchMore(filters: NewsFeedFiltersDTO) {
      const feedState = self[filters.state];

      if (feedState.fetchMoreStatus.pending) {
        return;
      }

      if (filters.state === 'published' && feedState.hasMoreActualEvents) {
        void self.fetchMoreActualEvents(filters);
      } else if (feedState.hasMore) {
        void self.fetchMoreFeeds(filters);
      }
    },

    getById: flow(function* (id: string) {
      self._getByIdAbortController?.abort();
      self._getByIdAbortController = new AbortController();
      const { signal } = self._getByIdAbortController;

      const response: NewsFeedDTO = yield News.GetNewsById(id, signal);

      return response;
    }),

    create: flow(function* (data: CreateNewsFeedDTO) {
      const response: NewsFeedDTO = yield News.CreateNews(data);
      void self.fetchNewsCounts();

      return response;
    }),

    update: flow(function* (id: string, data: NewsFeedDTO) {
      const response: NewsFeedDTO = yield News.UpdateNews(id, data);
      void self.fetchNewsCounts();

      return response;
    }),

    delete: flow(function* (id: string, state: NewsFeedDTO['state']) {
      try {
        yield News.DeleteNews(id);
        void self.fetchNewsCounts();
        self.removeItem(id, state);
      } catch (error) {
        return Promise.reject(error);
      }
    }),
  }));

export interface INewsFeedStore extends Instance<typeof NewsFeedStore> {}
